import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6gh84be24agvuu3jeg";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot check
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("留言已提交，我们会尽快与你联系。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value).trim();
      if (str) payload.append(key, str);
    });

    setStatus("submitting");
    setMessage("正在提交...");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam") || responseText.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !isSpam) {
        setStatus("success");
        setMessage(serverMsg || "留言已提交，感谢你的反馈，我们会尽快与你联系。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  const inputClass =
    "w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400";

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8"
    >
      <h3 className="font-heading font-bold text-lg text-foreground-950">给我们留言</h3>
      <p className="text-sm text-foreground-500 mt-1 mb-5">有任何建议或合作意向，欢迎填写下方信息联系我们。</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cf-name" className="block text-sm text-foreground-700 mb-1.5">
            你的称呼
          </label>
          <input id="cf-name" name="name" type="text" required placeholder="请输入称呼" className={inputClass} />
        </div>
        <div>
          <label htmlFor="cf-email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱地址
          </label>
          <input id="cf-email" name="email" type="email" required placeholder="请输入邮箱" className={inputClass} />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-topic" className="block text-sm text-foreground-700 mb-1.5">
          留言主题
        </label>
        <select id="cf-topic" name="topic" className={inputClass} defaultValue="内容建议">
          <option value="内容建议">内容建议</option>
          <option value="版权合作">版权合作</option>
          <option value="使用反馈">使用反馈</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-message" className="block text-sm text-foreground-700 mb-1.5">
          留言内容
        </label>
        <textarea
          id="cf-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          placeholder="请输入你的留言（不超过 500 字）"
          className={`${inputClass} resize-none`}
        ></textarea>
      </div>

      {/* honeypot */}
      <input
        type="text"
        name="website_alt"
        className="verify-field-notes"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60"
      >
        <i className="ri-send-plane-line"></i>
        {status === "submitting" ? "提交中..." : "提交留言"}
      </button>

      {status !== "idle" && status !== "submitting" && (
        <p
          className={`mt-4 text-sm flex items-center gap-1.5 ${
            status === "success" ? "text-primary-600" : "text-accent-600"
          }`}
        >
          <i className={status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
          {message}
        </p>
      )}
    </form>
  );
}